import React from "react";
import type { Product } from "@/types";
import { useRouter } from "@/router/Router";
import { useI18n } from "@/i18n";
import { formatUzs } from "@/lib/format";
import { ProductImage } from "./ProductImage";
import { useConfig } from "@/lib/useConfig";
import { RoastDiamonds } from "./Flavor";
import { categoryLabel } from "@/lib/categories";
import { PriceTag } from "./Premium";

/** Units left of the product's primary (first in-stock) variant. */
export function unitsLeft(product: Product): number {
  return (product.variants.find((v) => v.stock > 0) ?? product.variants[0])?.stock ?? 0;
}

/** Shared tag logic for cards and the product page. */
export function productTag(product: Product, t: (k: string) => string): string | null {
  if (product.orderMode !== "buy_now") return t("catalog.underOrder");
  if (product.tags?.includes("microlot")) return t("common.microlotBadge");
  if (product.pickOfMonth) return t("common.pickOfMonth");
  if (product.tags?.includes("bestseller")) return t("common.bestsellerBadge");
  if (product.tags?.includes("new")) return t("common.newBadge");
  return null;
}

/** Stable soft background per product, so the grid reads like a styled shelf. */
export function toneFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 4;
}

export function ProductCard({ product, width = 160 }: { product: Product; width?: number | string }) {
  const { navigate } = useRouter();
  const { t, locale } = useI18n();
  const { scarcityThreshold, socialProofMin } = useConfig();
  const primaryVariant = product.variants[0];
  const inStock = product.variants.some((v) => v.stock > 0);
  const isUnderOrder = product.orderMode !== "buy_now";
  const showPackLabel = primaryVariant && primaryVariant.label !== "Стандарт";

  const tag = productTag(product, t);
  const left = unitsLeft(product);
  const scarce = !isUnderOrder && scarcityThreshold > 0 && left > 0 && left <= scarcityThreshold;
  const proof = (product.soldLastWeek ?? 0) >= socialProofMin ? product.soldLastWeek : 0;

  return (
    <button className="product-card" style={{ width }} onClick={() => navigate({ name: "product", slug: product.slug })}>
      <div className="product-card__frame" data-tone={toneFor(product.id)}>
        {tag && <span className="product-card__tag">{tag}</span>}
        <ProductImage src={product.images[0]} alt={product.name} category={product.category} className="product-card__image" width={400} />
        {scarce && <span className="product-card__scarcity">{t("product.unitsLeft", { count: left })}</span>}
      </div>
      <div className="product-card__info">
        <div className="product-card__meta">
          <span className="eyebrow">{categoryLabel(product, t)}</span>
          <RoastDiamonds level={product.flavor?.roastLevel} />
        </div>
        <div className="product-card__title">{product.name}</div>
        {!!proof && <div className="product-card__proof">{t("product.soldWeek", { count: proof })}</div>}
        <div className="product-card__desc">{product.shortDescription}</div>
        <div className="product-card__bottom">
          <div style={{ minWidth: 0 }}>
            {isUnderOrder && !(primaryVariant.priceUzs > 0) ? (
              <span className="price" style={{ fontSize: 13.5, fontWeight: 500 }}>
                {t("catalog.onRequest")}
              </span>
            ) : isUnderOrder ? (
              <>
                <PriceTag uzs={primaryVariant.priceUzs} />
                <small>{t("catalog.underOrder")}</small>
              </>
            ) : (
              <PriceTag uzs={primaryVariant.priceUzs} />
            )}
            {!isUnderOrder && !inStock ? (
              <small className="is-out">{t("product.outOfStock")}</small>
            ) : (
              showPackLabel && !isUnderOrder && <small>{primaryVariant.label}</small>
            )}
          </div>
          <span className="round-btn" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              {isUnderOrder || !inStock ? (
                <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              )}
            </svg>
          </span>
        </div>
      </div>
    </button>
  );
}
